import type {
  FlightPlayerScoresFragment,
  HoleScoreFragment,
} from '../../../__generated__/hooks'
import {
  parForHoles,
  sumForHoles,
  sumStablefordForHoles,
  sumStrokesForHoles,
} from '../helpers'

/**
 * Subtotal / total row at the foot of front-9, back-9, and grand total.
 * `emphasized` styles the Tot row distinctly (green background + larger
 * number) so it reads as the round summary.
 *
 * The "Par N" label is the full par of the segment — a fixed reference.
 *
 * The sub-line shows whatever the round is ranked on, because that is the
 * number the player is actually competing with:
 *
 *   STABLEFORD  -> the segment's points (`30 p`), matching the per-hole stamps
 *   NET_STROKES -> the segment's net total (`75 netto`)
 *
 * It used to show gross-to-par under NET_STROKES, which measured the wrong
 * thing: a 6-handicap round of 81 read `+9` when the ranked score is 75. Gross
 * stays the headline number — that is what a paper card totals — and the
 * ranked figure sits beneath it.
 */
const TotalRow = ({
  label,
  flight,
  holes,
  emphasized,
  showStableford,
}: {
  label: string
  flight: FlightPlayerScoresFragment[]
  holes: HoleScoreFragment[]
  emphasized?: boolean
  showStableford?: boolean
}) => {
  const holeNumbers = new Set(holes.map((h) => h.hole.number))
  const par = parForHoles(holes)
  return (
    <tr
      className="border-t border-ink-200"
      style={{
        background: emphasized ? 'var(--green-50)' : 'var(--cream-card)',
      }}
    >
      <th
        scope="row"
        className="text-left px-2 py-2 align-middle"
        style={{
          background: emphasized ? 'var(--green-50)' : 'var(--cream-card)',
        }}
      >
        <div
          className={`font-display text-[11px] font-extrabold ${
            emphasized ? 'text-green-900' : 'text-ink-700'
          }`}
          style={{
            letterSpacing: 'var(--ls-caps)',
            textTransform: 'uppercase',
          }}
        >
          {label}
        </div>
        <div className="font-mono num text-[10px] text-ink-500 leading-tight mt-0.5">
          Par {par}
        </div>
      </th>
      {flight.map((entry) => {
        const total = sumForHoles(entry, holeNumbers)
        const points = sumStablefordForHoles(entry, holeNumbers)
        const net = total - sumStrokesForHoles(entry, holeNumbers)
        return (
          <td
            key={entry.id}
            className="text-center px-1 py-2 font-mono num leading-tight"
          >
            <div
              className={`font-bold ${
                emphasized ? 'text-green-900 text-base' : 'text-ink-900 text-sm'
              }`}
            >
              {total || '—'}
            </div>
            {total > 0 ? (
              <div className="text-[10px] text-ink-500 mt-0.5">
                {showStableford ? `${points} p` : `${net} netto`}
              </div>
            ) : null}
          </td>
        )
      })}
    </tr>
  )
}

export default TotalRow
